import { useId, useMemo, useRef, useState } from "react";
import {
  ArrowLeft,
  ArrowRight,
  Download,
  PenLine,
  RotateCw,
  Scissors,
  Trash2,
} from "lucide-react";
import { formatBytes } from "@suite/core";
import { AdBanner, AdConsentLink, SocialLinks, useToast } from "@suite/ui";
import {
  STORAGE_KEY,
  downloadBytes,
  parseRanges,
  type LoadedDoc,
  type PageRef,
} from "./document.ts";
import { usePdfPages } from "./usePdfPages.ts";
import { Thumb } from "./Thumb.tsx";
import { EditView } from "./EditView.tsx";
import { SignatureModal } from "./SignatureModal.tsx";
import {
  loadSignatures,
  persistSignatures,
  signatureFromText,
  type SavedSignature,
} from "./signatures.ts";

type Mode = "organise" | "edit";
import { Explainer } from "./Explainer.tsx";

export { STORAGE_KEY };

const HOME_URL = "https://ottercraft.dev";

export function App() {
  const { toast } = useToast();
  const {
    docs,
    pages,
    setPages,
    state,
    addFiles,
    reset,
    clearError,
    renderPage,
    setFill,
    clearFills,
    addStamp,
    updateStamp,
    removeStamp,
  } = usePdfPages();
  const [selection, setSelection] = useState<Set<string>>(new Set());
  const [busy, setBusy] = useState(false);
  const [splitInput, setSplitInput] = useState("");
  const [mode, setMode] = useState<Mode>("organise");
  const [focusPageId, setFocusPageId] = useState<string | null>(null);
  const [flatten, setFlatten] = useState(false);
  const [signatures, setSignatures] = useState<SavedSignature[]>(() => loadSignatures());
  const [placing, setPlacing] = useState<SavedSignature | null>(null);
  const [sigModal, setSigModal] = useState<{ open: boolean; target: { pageId: string; rect: [number, number, number, number] } | null }>({
    open: false,
    target: null,
  });
  const [textStamp, setTextStamp] = useState("");

  const fieldCount = useMemo(
    () => [...docs.values()].reduce((n, d) => n + new Set(d.fields.map((f) => f.name)).size, 0),
    [docs],
  );
  const filledCount = useMemo(
    () => [...docs.values()].reduce((n, d) => n + Object.keys(d.fills).length, 0),
    [docs],
  );
  const stampCount = useMemo(() => pages.reduce((n, p) => n + p.stamps.length, 0), [pages]);
  const mustFlatten = docs.size > 1 && fieldCount > 0;

  const openEdit = (pageId?: string) => {
    setMode("edit");
    setFocusPageId(pageId ?? null);
  };

  const useSignature = (signature: SavedSignature, isNew: boolean) => {
    if (isNew) {
      setSignatures((prev) => {
        const next = [signature, ...prev].slice(0, 6);
        persistSignatures(next);
        return next;
      });
    }
    const target = sigModal.target;
    setSigModal({ open: false, target: null });
    if (target) {
      // Clicked a signature field: drop the artwork straight into it.
      const page = pages.find((p) => p.id === target.pageId);
      if (page) {
        setPlacing(signature);
        // EditView fits it into the field on the next render via onSignatureField → place();
        // simplest is to let the user click the field again, but we can do it now:
        placeInto(page, target.rect, signature);
        setPlacing(null);
        return;
      }
    }
    setPlacing(signature);
    if (mode !== "edit") openEdit();
  };

  const placeInto = (page: PageRef, rect: [number, number, number, number], signature: SavedSignature) => {
    // Fit the artwork inside the field rectangle in PDF space, centred.
    const rw = rect[2] - rect[0];
    const rh = rect[3] - rect[1];
    const w = Math.min(rw, rh * signature.aspect);
    const h = w / signature.aspect;
    const rot = (((page.intrinsicRotation ?? 0) + page.rotation) % 360 + 360) % 360;
    // Field rects are axis-aligned in PDF space; anchor by the corner that is
    // the lower-left as displayed under the current rotation.
    const cx = rect[0] + rw / 2;
    const cy = rect[1] + rh / 2;
    const rad = (rot * Math.PI) / 180;
    const ux = Math.cos(rad), uy = Math.sin(rad), vx = -Math.sin(rad), vy = Math.cos(rad);
    const dw = rot % 180 === 0 ? w : h;
    const dh = rot % 180 === 0 ? h : w;
    const x = cx - (dw / 2) * ux - (dh / 2) * vx;
    const y = cy - (dw / 2) * uy - (dh / 2) * vy;
    addStamp(page.id, {
      id: `st_${Math.random().toString(36).slice(2, 10)}`,
      sourceId: signature.id,
      png: signature.png,
      aspect: signature.aspect,
      x,
      y,
      width: dw,
      height: dh,
      rotation: rot,
    });
  };

  const deleteSignature = (id: string) => {
    setSignatures((prev) => {
      const next = prev.filter((s) => s.id !== id);
      persistSignatures(next);
      return next;
    });
  };

  const placeText = (text: string) => {
    void signatureFromText(text, '"Manrope", ui-sans-serif, system-ui, sans-serif', 600).then((art) => {
      if (!art) return;
      setPlacing(art);
      if (mode !== "edit") openEdit();
    });
  };

  const totalSize = useMemo(
    () => [...docs.values()].reduce((sum, d) => sum + d.size, 0),
    [docs],
  );

  const move = (index: number, delta: number) => {
    setPages((prev) => {
      const target = index + delta;
      if (target < 0 || target >= prev.length) return prev;
      const next = [...prev];
      const [moved] = next.splice(index, 1);
      if (moved) next.splice(target, 0, moved);
      return next;
    });
  };

  const rotate = (pageIds: string[], delta: number) =>
    setPages((prev) =>
      prev.map((p) =>
        pageIds.includes(p.id)
          ? { ...p, rotation: ((p.rotation + delta) % 360 + 360) % 360 }
          : p,
      ),
    );

  const remove = (pageIds: string[]) => {
    setPages((prev) => {
      for (const page of prev) {
        if (pageIds.includes(page.id)) page.bitmap?.close();
      }
      return prev.filter((p) => !pageIds.includes(p.id));
    });
    setSelection(new Set());
  };

  const clearAll = () => {
    reset();
    setSelection(new Set());
    setSplitInput("");
    setMode("organise");
    setPlacing(null);
  };

  const exportPages = async (subset: PageRef[], filename: string) => {
    if (subset.length === 0) {
      toast("No pages to export", "warning");
      return;
    }
    setBusy(true);
    try {
      // pdf-lib is ~160 KB gzipped and is only needed at export time, so it is
      // fetched on first use rather than shipped in the initial bundle.
      const { buildPdf } = await import("./build.ts");
      const { bytes, warnings } = await buildPdf(subset, docs, { flattenForms: flatten || mustFlatten });
      downloadBytes(bytes, filename);
      toast(warnings[0] ? `${filename} ready — ${warnings[0]}` : `${filename} ready`, warnings.length > 0 ? "warning" : "success");
    } catch (error) {
      toast(
        error instanceof Error ? error.message : "Could not build the PDF",
        "warning",
      );
    } finally {
      setBusy(false);
    }
  };

  const selectedPages = pages.filter((p) => selection.has(p.id));
  const rangePages = parseRanges(splitInput, pages.length);
  const baseName =
    docs.size === 1
      ? ([...docs.values()][0]?.name ?? "document").replace(/\.pdf$/i, "")
      : "combined";

  return (
    <div className="grain relative min-h-dvh overflow-hidden bg-cream font-sans text-ink">
      <div className="relative z-10 mx-auto flex min-h-dvh w-full max-w-[1080px] flex-col px-5 pb-8 pt-0 sm:px-8 lg:px-10">
        {/* Header */}
        <header className="flex items-stretch justify-between gap-2">
          <div className="flex min-w-0 flex-1 items-baseline gap-3.5">
            <h1 className="flex shrink-0 items-center gap-2.5 pt-6 font-serif text-[28px] font-semibold leading-none text-pine sm:text-[34px]">
              <img src="/logo.png" alt="" width="34" height="34" className="size-[28px] sm:size-[34px]" />
              OtterPDF
              <span className="sr-only">: merge, split and rotate PDFs in your browser without uploading</span>
            </h1>
            <div className="flex flex-1 items-end self-stretch border-b border-rule pb-2.5">
              <p className="hidden text-[13px] text-muted sm:block">
                merge, split, reorder and rotate — all on your device
              </p>
            </div>
          </div>
          <StatusBar pages={pages.length} files={docs.size} loading={state.loading} />
        </header>

        {/* Headline + mascot */}
        <section className="relative mt-5 lg:mt-6">
          <img
            src="/otter/otter-mascot.png"
            alt=""
            aria-hidden="true"
            className="bob pointer-events-none absolute z-0 right-2 top-0 hidden h-[128px] w-auto drop-shadow-[0_10px_12px_rgba(46,30,16,0.2)] sm:block lg:right-8 lg:-top-1 lg:h-[150px]"
          />
          <h2 className="relative z-10 max-w-[820px] font-serif text-[40px] font-semibold leading-[1.0] tracking-[-0.01em] text-pine sm:text-[56px] lg:text-[64px] lg:leading-[0.98]">
            <Headline pages={pages.length} files={docs.size} loading={state.loading} mode={mode} />
          </h2>
        </section>

        {/* Main */}
        <div className="mt-6 grid gap-6 lg:grid-cols-[300px_minmax(0,1fr)] lg:gap-8">
          <div className="order-2 flex flex-col gap-5 lg:order-1">
            <ExportCard
              pageCount={pages.length}
              size={totalSize}
              busy={busy}
              onDownload={() => void exportPages(pages, `${baseName}-edited.pdf`)}
              onClear={clearAll}
              flatten={mustFlatten ? true : flatten}
              flattenLocked={mustFlatten}
              onFlatten={fieldCount > 0 ? setFlatten : undefined}
              edits={filledCount + stampCount}
            />
            {mode === "edit" ? (
              <>
                <SignCard
                  signatures={signatures}
                  placing={placing}
                  onPick={(s) => setPlacing(s)}
                  onNew={() => setSigModal({ open: true, target: null })}
                  onDelete={deleteSignature}
                  text={textStamp}
                  onText={setTextStamp}
                  onPlaceText={() => placeText(textStamp)}
                  onPlaceDate={() => placeText(new Date().toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" }))}
                />
                <FormCard
                  fields={fieldCount}
                  filled={filledCount}
                  files={docs.size}
                  onClear={() => clearFills()}
                />
              </>
            ) : (
              <>
            <SplitCard
              total={pages.length}
              value={splitInput}
              onChange={setSplitInput}
              matched={rangePages.length}
              selected={selectedPages.length}
              busy={busy}
              onExportRange={() =>
                void exportPages(
                  rangePages
                    .map((position) => pages[position - 1])
                    .filter((p): p is PageRef => p !== undefined),
                  `${baseName}-pages-${splitInput.replace(/[^\d,-]/g, "")}.pdf`,
                )
              }
              onExportSelected={() =>
                void exportPages(selectedPages, `${baseName}-selection.pdf`)
              }
            />
            <StackCard docs={[...docs.values()]} onFill={() => openEdit()} />
              </>
            )}
          </div>

          <div className="order-1 flex min-w-0 flex-col gap-5 lg:order-2">
            {mode === "organise" ? (
              <PondDrop
                empty={pages.length === 0}
                disabled={state.loading}
                onFiles={(files) => void addFiles(files)}
              />
            ) : null}

            {state.error ? (
              <p className="flex items-start justify-between gap-3 rounded-[12px] border border-danger/40 bg-surface px-4 py-3 text-[13px] leading-[1.5] text-danger">
                <span>{state.error}</span>
                <button type="button" className="link shrink-0 font-semibold" onClick={clearError}>
                  dismiss
                </button>
              </p>
            ) : null}

            <PagesLedger
              pages={pages}
              docs={docs}
              loading={state.loading}
              mode={mode}
              onMode={(m) => {
                setMode(m);
                setFocusPageId(null);
                if (m === "organise") setPlacing(null);
              }}
              onOpen={openEdit}
              selection={selection}
              onSelectAll={() =>
                setSelection(
                  selection.size === pages.length
                    ? new Set()
                    : new Set(pages.map((p) => p.id)),
                )
              }
              onToggle={(pageId, checked) =>
                setSelection((prev) => {
                  const next = new Set(prev);
                  if (checked) next.add(pageId);
                  else next.delete(pageId);
                  return next;
                })
              }
              onMove={move}
              onRotate={(ids) => rotate(ids, 90)}
              onRemove={remove}
            />
            {mode === "edit" && pages.length > 0 ? (
              <EditView
                pages={pages}
                docs={docs}
                renderPage={renderPage}
                setFill={setFill}
                addStamp={addStamp}
                updateStamp={updateStamp}
                removeStamp={removeStamp}
                placing={placing}
                onPlaced={() => setPlacing(null)}
                onSignatureField={(pageId, rect) => setSigModal({ open: true, target: { pageId, rect } })}
                focusPageId={focusPageId}
              />
            ) : null}
          </div>
        </div>

        <SignatureModal
          open={sigModal.open}
          onOpenChange={(open) => setSigModal((s) => ({ open, target: open ? s.target : null }))}
          saved={signatures}
          onUse={useSignature}
          onDelete={deleteSignature}
        />

        {/* Ad — directly under the tool. Renders nothing until a client id, slot and consent all exist. */}
        <AdBanner
          format="responsive"
          slot={import.meta.env["VITE_ADSENSE_SLOT"] as string | undefined}
          className="mx-auto mt-12 max-w-[728px]"
        />

        {/* What this is, how it works, FAQ — static content, also prerendered for crawlers. */}
        <Explainer />

        {/* Footer */}
        <footer className="mt-16 flex flex-col gap-6 border-t border-rule pt-6">
          <ul className="flex min-w-0 flex-wrap items-end gap-x-8 gap-y-4">
            <Fact n="01" text="Files never leave your device" img="/otter/otter-lock.png" />
            <Fact n="02" text="Nothing uploaded, nothing stored" img="/otter/otter-stream.png" />
            <Fact n="03" text="Merge, split, reorder, rotate" img="/otter/otter-p2p.png" />
          </ul>
          <nav aria-label="Site" className="flex flex-wrap items-center justify-between gap-x-5 gap-y-3 border-t border-rule/60 pt-4">
            <a
              href={HOME_URL}
              className="flex h-8 shrink-0 items-center gap-1.5 whitespace-nowrap rounded-full border border-rule bg-surface pl-2 pr-3 text-[13px] font-medium text-soft transition-colors hover:border-pine hover:text-pine"
            >
              <img src="/logo.png" alt="" width="18" height="18" className="size-[18px]" />
              More tools
            </a>
            <SocialLinks className="mr-auto" />
            <p className="flex gap-4 whitespace-nowrap text-[12px] text-muted">
              <a href={`${HOME_URL}/about`} className="link">About</a>
              <a href={`${HOME_URL}/contact`} className="link">Contact</a>
              <a href="/privacy" className="link">Privacy</a>
              <a href="/terms" className="link">Terms</a>
              <AdConsentLink />
            </p>
          </nav>
        </footer>
      </div>
    </div>
  );
}

// ---- header pieces --------------------------------------------------------

function StatusBar({ pages, files, loading }: { pages: number; files: number; loading: boolean }) {
  const dot = loading ? "bg-cream/70 breathe" : pages > 0 ? "bg-[#8fd17a]" : "bg-cream/70";
  const label = loading
    ? "reading pages…"
    : pages === 0
      ? "no pages yet"
      : `${pages} ${pages === 1 ? "page" : "pages"}`;
  // Phones can't fit the wordmark, the count and the file tally on one line.
  const detail = !loading && pages > 0 ? ` · ${files} ${files === 1 ? "file" : "files"}` : "";
  return (
    <div className="-mr-5 flex h-[46px] shrink-0 items-center gap-3 rounded-bl-[20px] bg-teal pl-3 pr-5 text-cream sm:pl-4 sm:pr-6 shadow-[0_6px_14px_-8px_rgba(31,63,57,0.6)] sm:-mr-8 lg:-mr-10">
      <img src="/otter/otter-avatars.png" alt="" aria-hidden="true" className="h-7 w-auto" />
      <span className={`inline-block size-2 rounded-full ${dot}`} aria-hidden="true" />
      <span className="font-mono text-[12px] uppercase tracking-[0.14em]">
        {label}
        <span className="hidden sm:inline">{detail}</span>
      </span>
    </div>
  );
}

function Headline({ pages, files, loading, mode }: { pages: number; files: number; loading: boolean; mode: Mode }) {
  if (mode === "edit" && pages > 0) {
    return (
      <>
        Fill it in, sign it, <em className="ripple">make it yours.</em>
      </>
    );
  }
  if (loading && pages === 0) {
    return (
      <>
        Paddling through your pages, <em className="ripple">one moment.</em>
      </>
    );
  }
  if (pages === 0) {
    return (
      <>
        Drop a PDF in the pond,
        <br />
        <em className="ripple">shuffle its pages.</em>
      </>
    );
  }
  if (files > 1) {
    return (
      <>
        {files} files, <em className="ripple">one tidy PDF.</em>
      </>
    );
  }
  return (
    <>
      {pages} {pages === 1 ? "page" : "pages"}, <em className="ripple">ready to shuffle.</em>
    </>
  );
}

// ---- left column ----------------------------------------------------------

function ExportCard({
  pageCount,
  size,
  busy,
  onDownload,
  onClear,
  flatten,
  flattenLocked,
  onFlatten,
  edits,
}: {
  pageCount: number;
  size: number;
  busy: boolean;
  onDownload: () => void;
  onClear: () => void;
  flatten: boolean;
  flattenLocked: boolean;
  onFlatten: ((v: boolean) => void) | undefined;
  edits: number;
}) {
  return (
    <div className="overflow-hidden rounded-[12px] border border-rule bg-surface shadow-[0_1px_0_var(--color-rule)]">
      <div className="flex items-center justify-between border-b border-rule px-4 py-2.5">
        <span className="eyebrow">Export</span>
        <span className="min-w-[22px] rounded-full bg-pine/10 px-2 py-0.5 text-center font-mono text-[11px] font-semibold text-pine">
          {pageCount}
        </span>
      </div>
      <div className="flex flex-col gap-3 p-4">
        <TealPill onClick={onDownload} disabled={busy || pageCount === 0} className="w-full">
          <Download className="size-3.5" />
          {busy ? "Building…" : "Download PDF"}
        </TealPill>
        {pageCount > 0 ? (
          <p className="flex items-baseline justify-between text-[12px] text-soft">
            <span>
              {pageCount} {pageCount === 1 ? "page" : "pages"} in the order shown
              {edits > 0 ? ` · ${edits} ${edits === 1 ? "edit" : "edits"}` : ""}
            </span>
            <span className="font-mono text-[11px] text-muted">{formatBytes(size)}</span>
          </p>
        ) : (
          <p className="font-serif text-[15px] italic text-muted">
            nothing to export yet — drop a PDF first
          </p>
        )}
        {onFlatten ? (
          <label className={`flex items-start gap-2 text-[12px] leading-[1.45] text-soft ${flattenLocked ? "opacity-70" : ""}`}>
            <input
              type="checkbox"
              className="mt-0.5 size-3.5 accent-pine"
              checked={flatten}
              disabled={flattenLocked}
              onChange={(e) => onFlatten(e.target.checked)}
            />
            <span>
              Flatten form fields
              <span className="block text-[11px] text-muted">
                {flattenLocked
                  ? "Always on when combining files — fields can't be carried between documents."
                  : "Bakes the values into the page so nobody can change them."}
              </span>
            </span>
          </label>
        ) : null}
        {pageCount > 0 ? (
          <button
            type="button"
            onClick={onClear}
            className="link self-start text-[12px] font-semibold text-muted transition-colors hover:text-danger"
          >
            Clear everything
          </button>
        ) : null}
      </div>
    </div>
  );
}

function SignCard({
  signatures,
  placing,
  onPick,
  onNew,
  onDelete,
  text,
  onText,
  onPlaceText,
  onPlaceDate,
}: {
  signatures: SavedSignature[];
  placing: SavedSignature | null;
  onPick: (s: SavedSignature) => void;
  onNew: () => void;
  onDelete: (id: string) => void;
  text: string;
  onText: (v: string) => void;
  onPlaceText: () => void;
  onPlaceDate: () => void;
}) {
  return (
    <div className="overflow-hidden rounded-[12px] border border-rule bg-surface shadow-[0_1px_0_var(--color-rule)]">
      <div className="flex items-center justify-between border-b border-rule px-4 py-2.5">
        <span className="eyebrow">Sign</span>
        <PenLine className="size-3.5 text-muted" aria-hidden="true" />
      </div>
      <div className="flex flex-col gap-3 p-4">
        {signatures.length > 0 ? (
          <ul className="grid grid-cols-2 gap-2">
            {signatures.map((s) => (
              <li key={s.id} className="group relative">
                <button
                  type="button"
                  onClick={() => onPick(s)}
                  title="Place this signature"
                  className={`flex h-14 w-full items-center justify-center rounded-[8px] border bg-[#fbfaf5] p-2 transition-colors ${
                    placing?.id === s.id ? "border-teal-deep ring-2 ring-teal/30" : "border-rule hover:border-pine"
                  }`}
                >
                  <img src={s.dataUrl} alt="Saved signature" className="max-h-full max-w-full object-contain" />
                </button>
                <button
                  type="button"
                  aria-label="Delete this saved signature"
                  onClick={() => onDelete(s.id)}
                  className="absolute -right-1.5 -top-1.5 flex size-5 items-center justify-center rounded-full border border-rule bg-surface text-[12px] leading-none text-muted opacity-0 shadow transition-opacity hover:text-danger group-hover:opacity-100 focus:opacity-100"
                >
                  ×
                </button>
              </li>
            ))}
          </ul>
        ) : null}
        <TealPill onClick={onNew} className="w-full">
          <PenLine className="size-3.5" />
          {signatures.length > 0 ? "New signature" : "Add your signature"}
        </TealPill>
        <p className="text-[11px] leading-[1.5] text-muted">
          {placing
            ? "Now click on the page where it should go."
            : "Pick a signature, then click on the page. Drag to move, corner to resize."}
        </p>
        <div className="flex flex-col gap-1.5 border-t border-rule pt-3">
          <span className="text-[12px] font-medium text-soft">Add text</span>
          <div className="flex gap-1.5">
            <input
              type="text"
              value={text}
              onChange={(e) => onText(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && text.trim()) onPlaceText();
              }}
              placeholder="Name, initials, place…"
              className="h-9 min-w-0 flex-1 rounded-[8px] border border-rule bg-[#fbfaf5] px-3 text-[13px] text-ink placeholder:text-muted/70 focus:border-teal-deep focus:outline-none focus:ring-2 focus:ring-teal/30"
            />
            <button
              type="button"
              disabled={!text.trim()}
              onClick={onPlaceText}
              className="flex h-9 items-center rounded-full border border-rule bg-surface px-3 text-[12px] font-medium text-soft transition-colors hover:border-pine hover:text-pine disabled:opacity-40"
            >
              Place
            </button>
          </div>
          <button type="button" onClick={onPlaceDate} className="link self-start text-[12px] font-semibold text-pine">
            Place today&rsquo;s date
          </button>
        </div>
      </div>
    </div>
  );
}

function FormCard({
  fields,
  filled,
  files,
  onClear,
}: {
  fields: number;
  filled: number;
  files: number;
  onClear: () => void;
}) {
  return (
    <div className="overflow-hidden rounded-[12px] border border-rule bg-surface shadow-[0_1px_0_var(--color-rule)]">
      <div className="flex items-center justify-between border-b border-rule px-4 py-2.5">
        <span className="eyebrow">Form</span>
        <span className="min-w-[22px] rounded-full bg-pine/10 px-2 py-0.5 text-center font-mono text-[11px] font-semibold text-pine">
          {fields}
        </span>
      </div>
      <div className="flex flex-col gap-2 p-4">
        {fields === 0 ? (
          <p className="font-serif text-[15px] italic text-muted">
            no fillable fields in {files === 1 ? "this file" : "these files"} — you can still sign and add text
          </p>
        ) : (
          <>
            <p className="text-[12px] text-soft">
              {filled} of {fields} {fields === 1 ? "field" : "fields"} changed. Fields are highlighted on the pages — type straight into them.
            </p>
            {filled > 0 ? (
              <button type="button" onClick={onClear} className="link self-start text-[12px] font-semibold text-muted hover:text-danger">
                Reset all fields
              </button>
            ) : null}
          </>
        )}
      </div>
    </div>
  );
}

function SplitCard({
  total,
  value,
  onChange,
  matched,
  selected,
  busy,
  onExportRange,
  onExportSelected,
}: {
  total: number;
  value: string;
  onChange: (value: string) => void;
  matched: number;
  selected: number;
  busy: boolean;
  onExportRange: () => void;
  onExportSelected: () => void;
}) {
  const inputId = useId();
  const disabled = total === 0;
  const hint =
    total === 0
      ? "Positions in the page list, once you've added a PDF"
      : value.trim() === ""
        ? `Positions in the list, 1 to ${total}`
        : matched === 0
          ? "That range does not match any pages"
          : `${matched} ${matched === 1 ? "page" : "pages"} selected`;

  return (
    <div className="overflow-hidden rounded-[12px] border border-rule bg-surface shadow-[0_1px_0_var(--color-rule)]">
      <div className="flex items-center justify-between border-b border-rule px-4 py-2.5">
        <span className="eyebrow">Split</span>
        <Scissors className="size-3.5 text-muted" aria-hidden="true" />
      </div>
      <div className="flex flex-col gap-3 p-4">
        <div className="flex flex-col gap-1.5">
          <label htmlFor={inputId} className="text-[12px] font-medium text-soft">
            Page range
          </label>
          <input
            id={inputId}
            type="text"
            inputMode="numeric"
            placeholder="1-3, 7, 10-"
            value={value}
            disabled={disabled}
            onChange={(e) => onChange(e.target.value)}
            className="h-9 w-full rounded-[8px] border border-rule bg-[#fbfaf5] px-3 font-mono text-[13px] text-ink placeholder:text-muted/70 focus:border-teal-deep focus:outline-none focus:ring-2 focus:ring-teal/30 disabled:opacity-50"
          />
          <p className={`text-[11px] ${matched === 0 && value.trim() !== "" ? "text-danger" : "text-muted"}`}>
            {hint}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
          <button
            type="button"
            disabled={busy || matched === 0}
            onClick={onExportRange}
            className="link text-[12px] font-semibold text-pine disabled:opacity-40"
          >
            Export range ↓
          </button>
          {selected > 0 ? (
            <button
              type="button"
              disabled={busy}
              onClick={onExportSelected}
              className="link text-[12px] font-semibold text-pine disabled:opacity-40"
            >
              Export {selected} selected ↓
            </button>
          ) : null}
        </div>
      </div>
    </div>
  );
}

function StackCard({ docs, onFill }: { docs: LoadedDoc[]; onFill: () => void }) {
  const fieldNames = docs.reduce((n, d) => n + new Set(d.fields.map((f) => f.name)).size, 0);
  return (
    <div className="overflow-hidden rounded-[12px] border border-rule bg-surface shadow-[0_1px_0_var(--color-rule)]">
      <div className="flex items-center justify-between border-b border-rule px-4 py-2.5">
        <span className="eyebrow">In the stack</span>
        <span className="min-w-[22px] rounded-full bg-pine/10 px-2 py-0.5 text-center font-mono text-[11px] font-semibold text-pine">
          {docs.length}
        </span>
      </div>
      <div className="flex flex-col gap-3 p-4">
        {docs.map((doc) => (
          <div key={doc.id} className="flex items-center gap-2.5">
            <span className="flex size-8 shrink-0 items-center justify-center rounded-[7px] border border-teal/40 bg-teal/12 text-teal-deep">
              <FileIcon />
            </span>
            <span className="flex min-w-0 flex-1 flex-col">
              <span className="truncate font-serif text-[16px] leading-tight text-pine-deep" title={doc.name}>
                {doc.name}
              </span>
              <span className="truncate font-mono text-[9px] uppercase tracking-[0.12em] text-pine-deep/55">
                {doc.pageCount} {doc.pageCount === 1 ? "page" : "pages"} · {formatBytes(doc.size)}
                {doc.fields.length > 0 ? ` · ${new Set(doc.fields.map((f) => f.name)).size} form fields` : ""}
              </span>
            </span>
          </div>
        ))}
        {docs.length === 0 ? (
          <p className="font-serif text-[15px] italic text-muted">
            no files yet — drop one or more PDFs to begin
          </p>
        ) : null}
        {fieldNames > 0 ? (
          <button type="button" onClick={onFill} className="link self-start text-[12px] font-semibold text-pine">
            Fill in the form →
          </button>
        ) : docs.length > 0 ? (
          <button type="button" onClick={onFill} className="link self-start text-[12px] font-semibold text-pine">
            Sign or add text →
          </button>
        ) : null}
        <p className="border-t border-rule pt-3 text-[11px] leading-[1.5] text-muted">
          Everything runs in this tab: pages are read by a worker on this page and the new PDF is
          assembled locally. Password-protected PDFs can&rsquo;t be opened; very large files may run
          out of memory on phones.
        </p>
      </div>
    </div>
  );
}

// ---- right column ---------------------------------------------------------

function PondDrop({
  empty,
  disabled,
  onFiles,
}: {
  empty: boolean;
  disabled: boolean;
  onFiles: (files: File[]) => void;
}) {
  const [dragging, setDragging] = useState(false);
  const depth = useRef(0);
  const inputId = useId();

  const accept = (list: FileList | null) => {
    if (!list || list.length === 0) return;
    onFiles(Array.from(list));
  };

  return (
    <div
      onDragEnter={(e) => {
        e.preventDefault();
        if (disabled) return;
        depth.current += 1;
        setDragging(true);
      }}
      onDragOver={(e) => e.preventDefault()}
      onDragLeave={(e) => {
        e.preventDefault();
        depth.current -= 1;
        if (depth.current <= 0) {
          depth.current = 0;
          setDragging(false);
        }
      }}
      onDrop={(e) => {
        e.preventDefault();
        depth.current = 0;
        setDragging(false);
        if (!disabled) accept(e.dataTransfer.files);
      }}
      className={`rounded-[18px] border-2 border-dashed transition-colors ${
        dragging ? "border-teal bg-teal/12" : "border-teal/45 bg-teal/[0.06]"
      } ${disabled ? "opacity-60" : ""}`}
    >
      <label
        htmlFor={inputId}
        className={`flex flex-col items-center justify-center gap-1 px-6 text-center ${
          empty ? "min-h-[150px] py-9" : "min-h-[84px] py-5"
        } ${disabled ? "cursor-not-allowed" : "cursor-pointer"}`}
      >
        <span
          className={`font-serif font-semibold leading-none text-pine ${
            empty ? "text-[26px] sm:text-[30px]" : "text-[20px] sm:text-[22px]"
          }`}
        >
          {empty ? "Drop PDFs here to begin" : "Drop more PDFs to add their pages"}
        </span>
        <span className="text-[13px] text-soft">
          or <span className="link font-medium text-teal-deep">choose from this device</span>
        </span>
        {empty ? (
          <span className="mt-1.5 font-mono text-[10px] uppercase tracking-[0.18em] text-muted">
            Nothing is uploaded — the file is read right here
          </span>
        ) : null}
      </label>
      <input
        id={inputId}
        type="file"
        multiple
        accept="application/pdf,.pdf"
        disabled={disabled}
        className="sr-only"
        onChange={(e) => {
          accept(e.target.files);
          e.target.value = "";
        }}
      />
    </div>
  );
}

function PagesLedger({
  pages,
  docs,
  loading,
  mode,
  onMode,
  onOpen,
  selection,
  onSelectAll,
  onToggle,
  onMove,
  onRotate,
  onRemove,
}: {
  pages: PageRef[];
  docs: Map<string, LoadedDoc>;
  loading: boolean;
  mode: Mode;
  onMode: (mode: Mode) => void;
  onOpen: (pageId: string) => void;
  selection: Set<string>;
  onSelectAll: () => void;
  onToggle: (pageId: string, checked: boolean) => void;
  onMove: (index: number, delta: number) => void;
  onRotate: (pageIds: string[]) => void;
  onRemove: (pageIds: string[]) => void;
}) {
  const allSelected = pages.length > 0 && selection.size === pages.length;
  const selected = [...selection];

  return (
    <div className="flex flex-col">
      <div className="mb-3 flex flex-wrap items-end justify-between gap-x-3 gap-y-2 border-b border-rule pb-2.5">
        <span className="flex items-center gap-3">
          <ModeSwitch mode={mode} onMode={onMode} disabled={pages.length === 0} />
          {pages.length > 0 && mode === "organise" ? (
            <span className="font-mono text-[11px] text-muted">
              {selection.size > 0 ? `${selection.size} of ${pages.length} selected` : `${pages.length} total`}
            </span>
          ) : null}
        </span>
        {mode === "edit" ? (
          <span className="font-mono text-[11px] text-muted">{pages.length} {pages.length === 1 ? "page" : "pages"}</span>
        ) : (
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={onSelectAll}
            disabled={pages.length === 0}
            className="flex h-8 items-center rounded-full border border-rule bg-surface px-3 text-[12px] font-medium text-soft transition-colors hover:border-pine hover:text-pine disabled:opacity-40 disabled:hover:border-rule disabled:hover:text-soft"
          >
            {allSelected ? "Select none" : "Select all"}
          </button>
          <button
            type="button"
            onClick={() => onRotate(selected)}
            disabled={selected.length === 0}
            className="flex h-8 items-center gap-1.5 rounded-full border border-rule bg-surface px-3 text-[12px] font-medium text-soft transition-colors hover:border-pine hover:text-pine disabled:opacity-40 disabled:hover:border-rule disabled:hover:text-soft"
          >
            <RotateCw className="size-3.5" />
            Rotate
          </button>
          <button
            type="button"
            onClick={() => onRemove(selected)}
            disabled={selected.length === 0}
            className="flex h-8 items-center gap-1.5 rounded-full border border-rule bg-surface px-3 text-[12px] font-medium text-soft transition-colors hover:border-danger hover:text-danger disabled:opacity-40 disabled:hover:border-rule disabled:hover:text-soft"
          >
            <Trash2 className="size-3.5" />
            Delete
          </button>
        </div>
        )}
      </div>

      {mode === "edit" ? null : pages.length === 0 ? (
        <p className="border-b border-rule py-5 font-serif text-[17px] italic text-muted">
          {loading
            ? "Reading and rendering pages…"
            : "The stack is empty — drop a PDF above and its pages will line up here."}
        </p>
      ) : (
        <ul className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4">
          {pages.map((page, index) => {
            const isSelected = selection.has(page.id);
            const source = docs.get(page.docId);
            return (
              <li
                key={page.id}
                className={`rounded-[12px] border p-2 shadow-[0_1px_0_var(--color-rule)] transition-colors ${
                  isSelected ? "border-teal bg-teal/12" : "border-rule bg-surface hover:border-pine/40"
                }`}
              >
                <label className="block cursor-pointer">
                  <span className="sr-only">Select page {index + 1} of the output</span>
                  <input
                    type="checkbox"
                    className="sr-only"
                    checked={isSelected}
                    onChange={(e) => onToggle(page.id, e.target.checked)}
                  />
                  <Thumb page={page} />
                </label>

                <div className="mt-2 flex items-center justify-between gap-1">
                  <span className="flex min-w-0 items-baseline gap-1.5">
                    <span className="font-mono text-[11px] font-semibold text-pine">
                      {String(index + 1).padStart(2, "0")}
                    </span>
                    {docs.size > 1 && source ? (
                      <span
                        className="truncate font-mono text-[9px] uppercase tracking-[0.1em] text-muted"
                        title={source.name}
                      >
                        {source.name.replace(/\.pdf$/i, "")}
                      </span>
                    ) : null}
                  </span>
                  <div className="flex shrink-0">
                    <IconButton
                      label={`Move page ${index + 1} earlier`}
                      disabled={index === 0}
                      onClick={() => onMove(index, -1)}
                    >
                      <ArrowLeft className="size-3.5" />
                    </IconButton>
                    <IconButton
                      label={`Move page ${index + 1} later`}
                      disabled={index === pages.length - 1}
                      onClick={() => onMove(index, 1)}
                    >
                      <ArrowRight className="size-3.5" />
                    </IconButton>
                    <IconButton label={`Rotate page ${index + 1}`} onClick={() => onRotate([page.id])}>
                      <RotateCw className="size-3.5" />
                    </IconButton>
                    <IconButton label={`Open page ${index + 1} to fill or sign`} onClick={() => onOpen(page.id)}>
                      <PenLine className="size-3.5" />
                    </IconButton>
                    <IconButton
                      label={`Delete page ${index + 1}`}
                      danger
                      onClick={() => onRemove([page.id])}
                    >
                      <Trash2 className="size-3.5" />
                    </IconButton>
                  </div>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

// ---- controls -------------------------------------------------------------

function ModeSwitch({ mode, onMode, disabled }: { mode: Mode; onMode: (m: Mode) => void; disabled: boolean }) {
  const item = (value: Mode, label: string) => (
    <button
      type="button"
      role="tab"
      aria-selected={mode === value}
      disabled={disabled}
      onClick={() => onMode(value)}
      className={`h-7 rounded-full px-3 font-mono text-[10px] uppercase tracking-[0.16em] transition-colors disabled:opacity-40 ${
        mode === value ? "bg-pine text-cream" : "text-muted hover:text-pine"
      }`}
    >
      {label}
    </button>
  );
  return (
    <div role="tablist" aria-label="View" className="flex items-center gap-0.5 rounded-full border border-rule bg-surface p-0.5">
      {item("organise", "Pages")}
      {item("edit", "Fill & sign")}
    </div>
  );
}

function TealPill({
  className = "",
  children,
  ...rest
}: React.ButtonHTMLAttributes<HTMLButtonElement>) {
  return (
    <button
      type="button"
      className={`flex h-9 items-center justify-center gap-2 rounded-full border-2 border-teal-deep bg-teal px-3.5 text-[12px] font-semibold text-cream shadow-[inset_0_1px_0_rgba(255,255,255,0.35),0_2px_5px_-2px_rgba(31,63,57,0.6)] transition-opacity hover:bg-teal-deep disabled:opacity-40 disabled:hover:bg-teal ${className}`}
      {...rest}
    >
      {children}
    </button>
  );
}

function IconButton({
  label,
  danger = false,
  children,
  ...rest
}: React.ButtonHTMLAttributes<HTMLButtonElement> & { label: string; danger?: boolean }) {
  return (
    <button
      type="button"
      aria-label={label}
      title={label}
      className={`flex size-7 items-center justify-center rounded-[6px] text-muted transition-colors disabled:opacity-30 ${
        danger ? "hover:bg-danger/10 hover:text-danger" : "hover:bg-pine/10 hover:text-pine"
      } disabled:hover:bg-transparent disabled:hover:text-muted`}
      {...rest}
    >
      {children}
    </button>
  );
}

// ---- footer ---------------------------------------------------------------

function Fact({ n, text, img }: { n: string; text: string; img: string }) {
  return (
    <li className="flex flex-col items-start gap-1.5">
      <img src={img} alt="" aria-hidden="true" className="h-9 w-auto" />
      <span className="flex items-baseline gap-2">
        <span className="font-mono text-[11px] text-pine">{n}</span>
        <span className="text-[13px] text-ink">{text}</span>
      </span>
    </li>
  );
}

// ---- icons ----------------------------------------------------------------

function FileIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8z" /><path d="M14 3v5h5" />
    </svg>
  );
}

